import {useEffect, useState, useRef} from "react";
import {useMutation} from "@tanstack/react-query";
import {toast} from "react-toastify";
import {AxiosError} from "axios";
import {fetchJudicialSelector, postJudicialGetOtp, resendJudicialOtp} from "@/api/user/home";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import CustomButton from "@/shared/components/kit/button";
import {CircularProgress} from "@mui/material";
import JudicialOtpTab from "../components/judicialOtpTab";

interface JudicialOtpStepProps {
    onSuccess: () => void;
}

// Define the OTP status type
interface OtpStatus {
    otp: boolean;
    error: boolean;
    success: boolean;
    reload: boolean;
}

const JudicialOtpStep = ({onSuccess}: JudicialOtpStepProps) => {
    const [judicialOtp, setJudicialOtp] = useState("");
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const hasSentOtp = useRef(false);
    const [otpStatus, setOtpStatus] = useState<OtpStatus>({
        otp: false,
        error: false,
        success: false,
        reload: false
    });

    const {mutate: sendOtp, isPending: isSendingOtp} = useMutation({
        mutationFn: fetchJudicialSelector,
        onSuccess: (response: any) => {
            console.log('res', response.data.status);
            setOtpSent(true);

            if (response?.data?.status === 'reload') {
                setOtpStatus({
                    otp: false,
                    error: false,
                    success: false,
                    reload: true
                });
                toast.info("نیاز به بارگذاری مجدد اطلاعات");
            } else if (response?.data?.status === 'resend') {
                setOtpStatus({
                    otp: false,
                    error: false,
                    success: false,
                    reload: true
                });
                toast.success(response.message || "کد امنیتی برای شما ارسال شد");
            } else if (response?.data?.status === 'success') {
                setOtpStatus({
                    otp: true,
                    error: false,
                    success: true,
                    reload: false
                });
                toast.success(response.message || "کد امنیتی برای شما ارسال شد");
            } else {
                setOtpStatus({
                    otp: false,
                    error: true,
                    success: false,
                    reload: false
                });
                toast.error(response?.message || "خطا در ارسال کد امنیتی");
            }
        },
        onError: (error: AxiosError<{ message: string }>) => {
            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false
            });
            toast.error(error?.response?.data?.message || "خطا در دریافت اطلاعات");
        },
    });

    // VERIFY OTP
    const {mutate: verifyOtp, isPending: isVerifying} = useMutation({
        mutationFn: postJudicialGetOtp,
        onSuccess: (response: any) => {
            if (response?.data?.status === 'success') {
                toast.success(response.message || "کد امنیتی با موفقیت تایید شد");
                onSuccess();
            } else if (response?.data?.status === 'reload') {
                setOtpStatus({
                    otp: false,
                    error: false,
                    success: false,
                    reload: true
                });
                // Instead of calling sendOtp directly, show a message and let user retry
                toast.info("نیاز به بارگذاری مجدد اطلاعات");
            } else if (response?.data?.status === 'resend') {
                setOtpStatus({
                    otp: false,
                    error: false,
                    success: false,
                    reload: true
                });
                toast.success(response.message || "کد امنیتی برای شما ارسال شد");
                // Don't call resendOtp recursively
            } else if (response?.data?.status === 'change') {
                setOtpStatus({
                    otp: true,
                    error: false,
                    success: true,
                    reload: false
                });
                toast.error(response.message || "کد وارد شده اشتباه است");
                // Clear the OTP input when it's wrong
                setJudicialOtp("");
            } else {
                setOtpStatus({
                    otp: false,
                    error: true,
                    success: false,
                    reload: false
                });
                toast.error(response?.message || "خطا در ارسال کد امنیتی");
            }
        },
        onError: (error: AxiosError<{ message: string }>) => {
            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false
            });
            toast.error(error?.response?.data?.message || "خطا در تایید کد");
        },
    });

    // RESEND OTP
    const {mutate: resendOtp, isPending: isResendingOtpMutation} = useMutation({
        mutationFn: resendJudicialOtp,
        onSuccess: (response: any) => {
            setIsResendingOtp(false);
            if (response?.data?.status === 'resend') {
                setOtpStatus({
                    otp: false,
                    error: false,
                    success: false,
                    reload: true
                });
                toast.success(response.message || "کد امنیتی برای شما ارسال شد");
                // Don't call resendOtp recursively
            } else if (response?.data?.status === 'success') {
                setOtpStatus({
                    otp: true,
                    error: false,
                    success: true,
                    reload: false
                });
                toast.success(response.message || "کد امنیتی برای شما ارسال شد");
            } else {
                setOtpStatus({
                    otp: false,
                    error: true,
                    success: false,
                    reload: false
                });
                toast.error(response?.message || "خطا در ارسال کد امنیتی");
            }
        },
        onError: (error: AxiosError<{ message: string }>) => {
            setIsResendingOtp(false);
            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false
            });
            toast.error(error?.response?.data?.message || "خطا در دریافت اطلاعات");
        },
    });

    // ONLY SEND OTP ONCE!
    useEffect(() => {
        if (!hasSentOtp.current) {
            hasSentOtp.current = true;
            console.log("🔵 JUDICIAL OTP STEP RENDERED - SENDING OTP ONCE!");
            sendOtp();
        }
    }, [sendOtp]);

    const handleContinue = () => {
        verifyOtp({otp: judicialOtp});
    };

    const handleResendOtp = () => {
        setJudicialOtp('');
        setIsResendingOtp(true);
        resendOtp();
    };

    // Handle Enter key press
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && isOtpValid && !isVerifying) {
            handleContinue();
        }
    };

    const isOtpValid = judicialOtp && judicialOtp.length >= 5;

    // Determine if we should show OTP component or error
    const shouldShowOtp = otpStatus.otp || otpStatus.success || (!otpStatus.error && !otpStatus.reload);
    const shouldShowError = otpStatus.error || otpStatus.reload;

    // Check if we're in the initial loading state
    const isInitialLoading = isSendingOtp && !otpSent;

    return (
        <StackKit direction="column" sx={{
            width: "100%",
            height: "100%",
            alignItems: shouldShowOtp ? "flex-start" : "center",
            justifyContent: shouldShowOtp ? "space-between" : "center",
            padding: '1rem'
        }} onKeyDown={handleKeyDown}>
            {shouldShowOtp && !otpStatus.error ? (
                <>
                    {isInitialLoading ? (
                        <StackKit sx={{width: "100%", height: "100%"}} direction={"row"} justifyContent={"center"} alignItems={"center"}>
                            <CircularProgress sx={{color: "#fff"}} size={50}/>
                        </StackKit>
                    ) : (
                        <JudicialOtpTab
                            valueOtp={judicialOtp}
                            setValueOtp={setJudicialOtp}
                            handleSubmit={handleContinue}
                            onResendOtp={handleResendOtp}
                            isResendingOtp={isResendingOtp || isResendingOtpMutation}
                            customTimerDuration={540}
                        />
                    )}

                    {!isInitialLoading && (
                        <StackKit direction="row" justifyContent="end" width="100%">
                            <CustomButton type="submit" onClick={handleContinue} variant="contained"
                                          disabled={!isOtpValid || isVerifying} sx={{mt: 3, width: "fit-content"}}>
                                <TypoGraphyKit variant="body1" sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minWidth: "50px"
                                }}>
                                    {isVerifying ? (
                                        <CircularProgress sx={{color: "#fff"}} size={22}/>
                                    ) : (
                                        "تایید کد امنیتی"
                                    )}
                                </TypoGraphyKit>
                            </CustomButton>
                        </StackKit>
                    )}
                </>
            ) : (
                <StackKit direction="row" justifyContent="end" width="100%">
                    <StackKit sx={{width: "100%", height: "100%"}} direction={"row"} justifyContent={"center"}
                              alignItems={"center"}>
                        <StackKit direction={"column"} sx={{width: "80%"}} alignItems={"center"}>
                            <TypoGraphyKit variant="h6" sx={{minHeight: "200px"}}>
                                {otpStatus.reload ? "در حال بارگذاری ..." : "خطا در ارتباط با سرویس اعتبار سنجی"}
                            </TypoGraphyKit>
                            {otpStatus.error && (
                                <CustomButton onClick={() => sendOtp()} variant="contained" sx={{mt: 2}}>
                                    تلاش مجدد
                                </CustomButton>
                            )}
                        </StackKit>
                    </StackKit>
                </StackKit>
            )}
        </StackKit>
    );
};

export default JudicialOtpStep;